import { useCallback, useMemo } from 'react';
import {
  mapFieldType,
  isFileType,
} from '../../../utils/formCrafter/fieldTypeUtils';
import { convertToFormCrafterSchema } from '../../../utils/formCrafter/schemaUtils';
import { parseFormData } from '../../../utils/formCrafter/formDataUtils';

// Re-export utilities for backward compatibility
export {
  mapFieldType,
  isFileType,
} from '../../../utils/formCrafter/fieldTypeUtils';
export { convertToFormCrafterSchema } from '../../../utils/formCrafter/schemaUtils';
export { parseFormData } from '../../../utils/formCrafter/formDataUtils';

/**
 * useFormCrafterSchema - Hook for normalizing and parsing FormCrafter schemas
 *
 * Provides utilities to:
 * - Convert various schema formats to normalized FormCrafter schema
 * - Parse filled form data from various structures
 * - Map field types between legacy and current formats
 *
 * @param {Array} subforms - Array of subform objects with json_object and id
 * @param {Object} filledForm - Previously filled form data (optional)
 * @param {number|string} formId - Form ID for nested data structure extraction (optional)
 * @returns {Object} - { schemas, formData, getSchemaForSubform, getDataForSubform }
 */
const useFormCrafterSchema = (
  subforms = [],
  filledForm = null,
  formId = null
) => {
  // Parse all subform schemas once
  const schemas = useMemo(() => {
    const result = {};
    subforms.forEach((subform) => {
      if (subform?.json_object) {
        result[subform.id] = convertToFormCrafterSchema(
          subform.json_object,
          subform.id
        );
      }
    });
    return result;
  }, [subforms]);

  // Parse filled form data once
  const formData = useMemo(() => {
    return parseFormData(filledForm, formId);
  }, [filledForm, formId]);

  // Get schema for a specific subform
  const getSchemaForSubform = useCallback(
    (subformId) => {
      return schemas[subformId] || null;
    },
    [schemas]
  );

  // Get data for a specific subform
  const getDataForSubform = useCallback(
    (subformId) => {
      const subformIdStr = String(subformId);
      return formData[subformIdStr] || formData[subformId] || {};
    },
    [formData]
  );

  return {
    schemas,
    formData,
    getSchemaForSubform,
    getDataForSubform,
  };
};

export default useFormCrafterSchema;
